import { useCallback, useState } from 'react'
import type { PointerEvent as ReactPointerEvent, RefObject } from 'react'
import isHTMLInputElement from 'lib/dom/isHTMLInputElement'

/** How far the pointer has to travel before a press on the selected tab becomes a drag. */
const DRAG_START_THRESHOLD = 4

/** LuxKit stamps this on the pill that slides between segmented options. */
const INDICATOR_SELECTOR = '.segmented-options-indicator'

interface Segment {
  input: HTMLInputElement
  /** Layout position within the options group, in the same space the indicator translates by. */
  offsetLeft: number
  offsetTop: number
  width: number
}

function getSegmentInput(label: HTMLLabelElement): HTMLInputElement | undefined {
  const input = label.querySelector('input[type="radio"]')
  return (!!input && isHTMLInputElement(input)) ? input : undefined
}

function getSegments(root: HTMLElement): Array<Segment> {
  return Array.from(root.querySelectorAll('label')).reduce<Array<Segment>>((segments, label) => {
    const input = getSegmentInput(label)
    if (input && !input.disabled) {
      segments.push({ input, offsetLeft: label.offsetLeft, offsetTop: label.offsetTop, width: label.offsetWidth })
    }
    return segments
  }, [])
}

function getNearestSegment(segments: Array<Segment>, centre: number): Segment {
  return segments.reduce((nearest, segment) => {
    const segmentDistance = Math.abs(segment.offsetLeft + (segment.width / 2) - centre)
    const nearestDistance = Math.abs(nearest.offsetLeft + (nearest.width / 2) - centre)
    return segmentDistance < nearestDistance ? segment : nearest
  })
}

function placeIndicator(indicator: HTMLElement, left: number, top: number, width: number) {
  indicator.style.transform = `translate(${left}px, ${top}px)`
  indicator.style.width = `${width}px`
}

interface TabDrag {
  isDragging: boolean
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void
}

/**
 * Lets the selected tab be dragged along the bar, the way a native segmented control behaves, rather
 * than only being tappable one tab at a time.
 *
 * This drives LuxKit's `SegmentedOptionsGroup` from the outside — it moves the group's own indicator
 * by hand for the length of the gesture and then commits through a click on the target radio, so the
 * group ends up in exactly the state a tap would have left it in. Two consequences worth knowing:
 *
 * - The group re-attaches its indicator in an effect with no dependency array, so any render clobbers
 *   a hand-placed indicator. Nothing here changes the selected value mid-drag, so it normally doesn't
 *   render at all; if the shell re-renders underneath us anyway, the next pointer move re-applies the
 *   position, costing at most a frame.
 * - A drag that ends where it started produces no value change, and so no render to put the indicator
 *   back — this restores it explicitly instead.
 *
 * Only the selected tab is a drag handle. A press on any other tab stays a plain tap.
 */
function useTripChatMobileTabDrag(regionRef: RefObject<HTMLDivElement | null>): TabDrag {
  const [isDragging, setIsDragging] = useState(false)

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const region = regionRef.current
    if (!region || !event.isPrimary || event.button !== 0) return

    const indicator = region.querySelector(INDICATOR_SELECTOR)
    const label = (event.target as Element).closest('label')
    if (!(indicator instanceof HTMLElement) || !label || !region.contains(label)) return

    const grabbedInput = getSegmentInput(label)
    if (!grabbedInput?.checked || grabbedInput.disabled) return

    const segments = getSegments(region)
    const grabbed = segments.find(segment => segment.input === grabbedInput)
    if (!grabbed || segments.length < 2) return

    const startX = event.clientX
    // Tabs are laid out right to left under RTL, so take the extents rather than the ends of the list.
    const trackStart = Math.min(...segments.map(segment => segment.offsetLeft))
    const trackEnd = Math.max(...segments.map(segment => segment.offsetLeft + segment.width))
    let hasStarted = false
    let selection = grabbed

    const moveIndicator = (clientX: number) => {
      const left = Math.min(
        Math.max(grabbed.offsetLeft + clientX - startX, trackStart),
        trackEnd - selection.width,
      )
      selection = getNearestSegment(segments, left + (selection.width / 2))

      indicator.style.transition = 'none'
      placeIndicator(indicator, left, grabbed.offsetTop, selection.width)
    }

    const handleMove = (moveEvent: PointerEvent) => {
      if (moveEvent.pointerId !== event.pointerId) return
      if (!hasStarted) {
        if (Math.abs(moveEvent.clientX - startX) < DRAG_START_THRESHOLD) return
        hasStarted = true
        setIsDragging(true)
      }
      moveIndicator(moveEvent.clientX)
    }

    const handleEnd = (endEvent: PointerEvent) => {
      if (endEvent.pointerId !== event.pointerId) return
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleEnd)
      window.removeEventListener('pointercancel', handleEnd)
      if (!hasStarted) return

      setIsDragging(false)
      // Hand the indicator's own transition back so it eases into its final place either way.
      indicator.style.transition = ''
      // A cancelled gesture keeps the tab it started on, so only a completed drag commits.
      if (endEvent.type !== 'pointercancel' && selection !== grabbed) {
        selection.input.click()
      } else {
        placeIndicator(indicator, grabbed.offsetLeft, grabbed.offsetTop, grabbed.width)
      }
    }

    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', handleEnd)
    window.addEventListener('pointercancel', handleEnd)
  }, [regionRef])

  return { isDragging, onPointerDown }
}

export default useTripChatMobileTabDrag
